import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, Spinner, WarningCircle } from "@phosphor-icons/react";
import { useAuth, API } from "@/contexts/AuthContext";
import { PlantusLogo } from "@/components/plantus/Logo";

export default function CheckoutSuccessPage() {
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", attempts: 0, paymentStatus: null });

  const poll = useCallback(
    async (attempt = 0) => {
      if (!sessionId) {
        setState({ status: "error", error: "Keine Session-ID in der URL." });
        return;
      }
      if (attempt > 8) {
        setState((s) => ({ ...s, status: "timeout" }));
        return;
      }
      try {
        const { data } = await axios.get(`${API}/stripe/checkout-status/${sessionId}`);
        setState((s) => ({ ...s, attempts: attempt + 1, paymentStatus: data.payment_status }));
        if (data.payment_status === "paid") {
          await refresh();
          setState({ status: "success", attempts: attempt + 1, paymentStatus: "paid" });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", attempts: attempt + 1 });
          return;
        }
        setTimeout(() => poll(attempt + 1), 1800);
      } catch (err) {
        setState({ status: "error", error: err.response?.data?.detail || "Prüfung fehlgeschlagen." });
      }
    },
    [sessionId, refresh]
  );

  useEffect(() => {
    document.title = "Plantus Pro · Bestätigung";
    poll(0);
  }, [poll]);

  return (
    <div className="min-h-screen bg-bone flex flex-col" data-testid="checkout-success-page">
      <header className="border-b border-bone-200/70 px-6 h-20 flex items-center">
        <Link to="/">
          <PlantusLogo size="lg" />
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full bg-white rounded-3xl border border-bone-200 p-10 text-center">
          {state.status === "polling" && (
            <>
              <Spinner size={48} className="mx-auto text-forest-700 animate-spin" />
              <h1 className="mt-6 font-heading text-2xl font-semibold text-forest-900">
                Zahlung wird geprüft…
              </h1>
              <p className="mt-2 text-sm text-[#4A524C]">
                Bitte einen Moment Geduld, Stripe bestätigt deine Buchung.
              </p>
            </>
          )}
          {state.status === "success" && (
            <>
              <CheckCircle size={48} weight="fill" className="mx-auto text-forest-600" />
              <h1 className="mt-6 font-heading text-2xl font-semibold text-forest-900">
                Willkommen bei Plantus Pro!
              </h1>
              <p className="mt-2 text-sm text-[#4A524C]">
                Dein Abo ist aktiv. Alle Mitarbeiter können ab jetzt unbegrenzt Anwendungen erfassen.
              </p>
              <Link
                to="/app/abo"
                data-testid="checkout-success-cta"
                className="mt-8 inline-flex items-center justify-center gap-2 bg-forest-600 hover:bg-forest-700 text-white font-medium text-sm px-6 py-3 rounded-full shadow-sm hover:shadow-md transition-all"
              >
                Zum Abo-Bereich
              </Link>
            </>
          )}
          {state.status === "timeout" && (
            <>
              <WarningCircle size={48} weight="fill" className="mx-auto text-amber-600" />
              <h1 className="mt-6 font-heading text-2xl font-semibold text-forest-900">
                Status noch nicht final
              </h1>
              <p className="mt-2 text-sm text-[#4A524C]">
                Stripe ist noch nicht durch. Aktualisiere die Seite in einer Minute oder prüfe später dein Abo.
              </p>
              <Link
                to="/app/abo"
                className="mt-8 inline-flex items-center justify-center gap-2 border border-forest-600/30 text-forest-700 font-medium text-sm px-6 py-3 rounded-full hover:bg-forest-50"
              >
                Zum Abo-Bereich
              </Link>
            </>
          )}
          {state.status === "expired" && (
            <>
              <WarningCircle size={48} weight="fill" className="mx-auto text-amber-600" />
              <h1 className="mt-6 font-heading text-2xl font-semibold text-forest-900">
                Sitzung abgelaufen
              </h1>
              <p className="mt-2 text-sm text-[#4A524C]">Bitte starte den Checkout erneut.</p>
              <Link
                to="/preise"
                className="mt-8 inline-flex items-center justify-center gap-2 bg-forest-600 hover:bg-forest-700 text-white font-medium text-sm px-6 py-3 rounded-full"
              >
                Erneut versuchen
              </Link>
            </>
          )}
          {state.status === "error" && (
            <>
              <WarningCircle size={48} weight="fill" className="mx-auto text-red-600" />
              <h1 className="mt-6 font-heading text-2xl font-semibold text-forest-900">
                Etwas ging schief
              </h1>
              <p className="mt-2 text-sm text-[#4A524C]">{state.error}</p>
              <Link
                to="/preise"
                className="mt-8 inline-flex items-center justify-center gap-2 bg-forest-600 hover:bg-forest-700 text-white font-medium text-sm px-6 py-3 rounded-full"
              >
                Zur Preisseite
              </Link>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
